// One list per signed-in user, so a shared computer never shows the previous person's patients.
const STORAGE_PREFIX = 'stabletrack.recentlyViewedPatients';
const MAX_ENTRIES = 10;

const keyFor = (uid: string) => `${STORAGE_PREFIX}.${uid}`;

export interface RecentPatient {
  id: string;
  name: string;
  patientId?: string;
  breed?: string;
  ownerName?: string;
  status?: string;
  viewedAt: number;
}

function read(uid: string | undefined): RecentPatient[] {
  if (!uid) return [];
  try {
    const raw = localStorage.getItem(keyFor(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(uid: string, list: RecentPatient[]) {
  try {
    localStorage.setItem(keyFor(uid), JSON.stringify(list));
  } catch {
    // localStorage quota / disabled — silently skip; UI degrades gracefully.
  }
}

/**
 * Push a patient onto the recently-viewed list. De-dupes by id (so revisiting
 * the same horse bumps it to the front) and trims to MAX_ENTRIES. Safe to
 * call on every chart mount.
 */
export function markPatientViewed(uid: string | undefined, p: Omit<RecentPatient, 'viewedAt'>) {
  if (!uid || !p.id) return;
  const list = read(uid);
  const without = list.filter((x) => x.id !== p.id);
  without.unshift({ ...p, viewedAt: Date.now() });
  write(uid, without.slice(0, MAX_ENTRIES));
  // Notify listeners in the same tab — storage events only fire across tabs.
  try {
    window.dispatchEvent(new CustomEvent('recentlyViewedPatients:change'));
  } catch {
    /* noop */
  }
}

export function getRecentlyViewed(uid: string | undefined): RecentPatient[] {
  return read(uid);
}

/** Drop every user's list, and the old unscoped one, when a session ends. */
export function clearRecentlyViewed() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(STORAGE_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage disabled: nothing was stored */
  }
}

/**
 * Hook-friendly subscription helper. Pass a callback; returns the unsubscribe.
 */
export function subscribeRecentlyViewed(cb: () => void): () => void {
  const handler = () => cb();
  window.addEventListener('recentlyViewedPatients:change', handler);
  // Also re-render on cross-tab updates so a horse viewed in another tab
  // surfaces here without a manual refresh.
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('recentlyViewedPatients:change', handler);
    window.removeEventListener('storage', handler);
  };
}
